<template>
    <v-sheet class="model-status" color="darkbase">
        <Transition name="agent-status">
        <div v-if="pending" :key="`pending:${statusText}`" class="model-status-progress" role="status" aria-live="polite">
            <span class="model-status-text">{{ statusText }}</span><span class="model-status-dots" aria-hidden="true">{{ dots }}</span>
        </div>
        <div v-else-if="error" :key="`error:${errorText}`" class="model-status-error">
            <p role="alert">{{ errorText }}</p>
            <v-btn
                v-if="uncertain"
                variant="tonal"
                color="color1"
                prepend-icon="fa-solid fa-arrows-rotate"
                aria-label="Check status"
                :loading="checking"
                :disabled="checking"
                @click="$emit('check-status')">
                <span class="d-none d-sm-inline">Check status</span>
            </v-btn>
            <v-btn v-else variant="text" prepend-icon="fa-solid fa-rotate-right" aria-label="Try again" :disabled="busy" @click="$emit('retry')">
                <span class="d-none d-sm-inline">Try again</span>
            </v-btn>
        </div>
        <div v-else-if="ready" key="ready" class="model-status-actions">
            <v-btn variant="text" prepend-icon="fa-solid fa-rotate-right" aria-label="Try again" :disabled="busy" @click="$emit('retry')">
                <span class="d-none d-sm-inline">Try again</span>
            </v-btn>
            <div class="model-status-actions-right">
                <v-btn variant="text" prepend-icon="fa-solid fa-download" aria-label="Download model" @click="$emit('download')">
                    <span class="d-none d-sm-inline">Download</span>
                </v-btn>
                <v-btn variant="text" prepend-icon="fa-solid fa-share-nodes" aria-label="Share model" @click="$emit('share')">
                    <span class="d-none d-sm-inline">Share</span>
                </v-btn>
            </div>
        </div>
        </Transition>
    </v-sheet>
</template>

<script>
function capitalize(text) {
    const value = String(text || '').trim()
    return value.charAt(0).toLocaleUpperCase() + value.slice(1)
}

export default {
    name: 'ModelStatus',
    props: {
        status: { type: String, default: 'Waiting for available agent' },
        pending: { type: Boolean, default: false },
        ready: { type: Boolean, default: false },
        busy: { type: Boolean, default: false },
        error: { type: String, default: '' },
        uncertain: { type: Boolean, default: false },
        checking: { type: Boolean, default: false }
    },
    emits: ['retry', 'check-status', 'download', 'share'],
    data() {
        return { dotCount: 1 }
    },
    computed: {
        statusText() { return capitalize(this.status).replace(/[.\u2026]+$/, '') },
        errorText() { return capitalize(this.error) },
        dots() { return '.'.repeat(this.dotCount) }
    },
    watch: {
        pending: {
            immediate: true,
            handler(pending) {
                clearInterval(this.dotsTimer)
                if (pending) this.dotsTimer = setInterval(() => { this.dotCount = this.dotCount % 3 + 1 }, 500)
            }
        }
    },
    beforeUnmount() {
        clearInterval(this.dotsTimer)
    }
}
</script>

<style scoped>
.model-status {
    position: relative;
    display: block;
    flex: none;
    width: 100%;
    min-height: 58px;
    padding: 12px 16px;
    border-top: 1px solid rgb(var(--v-theme-lightbase));
    color: rgb(var(--v-theme-secondary));
    font-size: 13px;
    text-align: left;
}

.model-status-progress {
    display: block;
    width: 100%;
    padding: 6px 0;
    text-align: left;
    overflow-wrap: anywhere;
}

.model-status-text {
    display: inline;
}

.model-status-dots {
    display: inline-block;
    width: 1.4em;
    text-align: left;
    white-space: nowrap;
}

.model-status-actions,
.model-status-actions-right {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
}

.model-status-actions {
    justify-content: space-between;
}

.model-status .v-btn {
    text-transform: none;
    letter-spacing: normal;
}

.model-status-error {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
}

.model-status-error p {
    margin: 0;
    overflow-wrap: anywhere;
    flex: 1 1 200px;
}

@media (max-width: 599.98px) {
    .model-status .v-btn {
        width: 44px;
        min-width: 44px;
        height: 44px;
        padding: 0;
    }

    .model-status :deep(.v-btn__prepend) {
        margin: 0;
    }
}
</style>
